import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  Textarea,
  Switch,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Card,
  CardHeader,
  CardBody,
  SimpleGrid,
  Badge,
  IconButton,
  Tooltip,
  Progress,
  useToast,
  Spinner,
  Flex,
  Spacer,
  Container,
  useColorModeValue
} from "@chakra-ui/react";
import { 
  ArrowBackIcon, 
  CopyIcon, 
  CheckIcon, 
  AddIcon,
  CalendarIcon,
  TimeIcon,
  InfoIcon
} from "@chakra-ui/icons";
import { useActionForm, useUser, useFindMany } from "@gadgetinc/react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { hasAnyRole } from "../lib/roleUtils";

export default function NewShift() {
  const navigate = useNavigate();
  const toast = useToast();
  const currentUser = useUser();
  const [currentStep, setCurrentStep] = useState(1);
  const [isRecurring, setIsRecurring] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  
  // Form background colors
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  // Fetch users for student/preceptor selection
  const [{ data: users, fetching: fetchingUsers }] = useFindMany(api.user, {
    select: {
      id: true,
      firstName: true,
      lastName: true,
      email: true,
      institutionRole: true,
      cohort: true,
      institution: true
    },
    filter: {
      approvalStatus: { equals: "approved" }
    }
  });

  // Initialize form with useActionForm
  const {
    register,
    submit: originalSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    reset,
    getValues
  } = useActionForm(api.shift.create, {
    defaultValues: {
      shift: {
        status: "scheduled",
        locked: false,
        createdBy: { _link: currentUser?.id }
      }
    },
    onSuccess: (result) => {
      toast({
        title: "Shift Created Successfully",
        description: `Shift has been scheduled for ${result.shiftDate}`,
        status: "success",
        duration: 5000,
        isClosable: true
      });
      navigate("/shifts");
    },
    onError: (error) => {
      toast({
        title: "Error Creating Shift",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true
      });
    }
  });

  // Helper function to convert datetime-local to ISO string
  const convertToISOString = (datetimeLocal) => {
    if (!datetimeLocal) return null;
    
    // datetime-local format: "2024-01-15T14:30"
    // We need to convert to: "2024-01-15T14:30:00.000Z"
    const date = new Date(datetimeLocal);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return null;
    
    // Convert to ISO string
    return date.toISOString();
  };

  // Helper function to parse user field values
  const parseUserField = (value) => {
    if (!value) return null;
    
    // If it's already an object, return as is
    if (typeof value === 'object') return value;
    
    // If it's a string ID, convert to { _link: id }
    if (typeof value === 'string' && value.length > 0) {
      return { _link: value };
    }
    
    return null;
  };

  // Helper function to format rich text fields
  const formatRichTextField = (value) => {
    if (!value) return null;
    if (typeof value === 'string') {
      return { markdown: value };
    }
    return value;
  };

  // Custom submit handler with data preprocessing
  const handleSubmit = async (event) => {
    if (event) {
      event.preventDefault();
    }

    const formData = getValues();
    
    // Preprocess the data
    const processedData = {
      ...formData,
      shift: {
        ...formData.shift,
        // Convert datetime fields
        shiftDate: convertToISOString(formData.shift?.shiftDate),
        endTime: convertToISOString(formData.shift?.endTime),
        
        // Parse user relationships
        student: parseUserField(formData.shift?.student),
        preceptor: parseUserField(formData.shift?.preceptor),
        
        // Format rich text fields
        objectives: formatRichTextField(formData.shift?.objectives),
        notes: formatRichTextField(formData.shift?.notes),
        preceptorNotes: formatRichTextField(formData.shift?.preceptorNotes),
        reflectionNotes: formatRichTextField(formData.shift?.reflectionNotes),
        
        // Ensure createdBy is properly set
        createdBy: { _link: currentUser?.id }
      }
    };

    try {
      await originalSubmit(processedData);
    } catch (error) {
      console.error('Submit error:', error);
    }
  };

  // Permission check
  const hasSchedulingPermission = currentUser && (
    hasAnyRole(currentUser.roles, ['admin', 'system-admin']) ||
    currentUser.institutionRole === "instructor" ||
    currentUser.institutionRole === "administrator"
  );

  // Watch form values for dynamic updates
  const watchedShiftType = watch("shift.shiftType");
  const watchedInstitution = watch("shift.institution");

  // Get unique institutions and cohorts from users
  const institutions = users ? [...new Set(users.map(u => u.institution).filter(Boolean))] : [];
  const cohorts = users ? [...new Set(users.map(u => u.cohort).filter(Boolean))] : [];

  // Filter users based on roles
  const students = (users?.filter(u => 
    u.institutionRole === "student" && 
    (watchedInstitution ? u.institution === watchedInstitution : true)
  ) || []).sort((a, b) => {
    const aLast = String(a?.lastName || "").toLowerCase();
    const bLast = String(b?.lastName || "").toLowerCase();
    if (aLast !== bLast) return aLast.localeCompare(bLast);

    const aFirst = String(a?.firstName || "").toLowerCase();
    const bFirst = String(b?.firstName || "").toLowerCase();
    if (aFirst !== bFirst) return aFirst.localeCompare(bFirst);

    const aEmail = String(a?.email || "").toLowerCase();
    const bEmail = String(b?.email || "").toLowerCase();
    return aEmail.localeCompare(bEmail);
  });
  
  const preceptors = users?.filter(u => 
    (u.institutionRole === "instructor" || u.institutionRole === "staff") &&
    (watchedInstitution ? u.institution === watchedInstitution : true)
  ) || [];

  // Handle copy from existing shift (placeholder)
  const handleCopyShift = () => {
    toast({
      title: "Copy Shift",
      description: "This feature will be available soon",
      status: "info",
      duration: 3000,
      isClosable: true
    });
  };

  // Handle save as template (placeholder)
  const handleSaveTemplate = () => {
    toast({
      title: "Save Template",
      description: "This feature will be available soon",
      status: "info",
      duration: 3000,
      isClosable: true
    });
  };

  // Handle save draft
  const handleSaveDraft = () => {
    setIsDraft(true);
    toast({
      title: "Draft Saved",
      description: "Your shift draft has been saved locally",
      status: "success",
      duration: 3000,
      isClosable: true
    });
  };

  // Steps configuration
  const steps = [
    { number: 1, title: "Basic Information", description: "Shift details and timing" },
    { number: 2, title: "Assignment", description: "Student and preceptor assignment" },
    { number: 3, title: "Additional Details", description: "Notes and special instructions" },
    { number: 4, title: "Review", description: "Review and submit" }
  ];

  const progress = (currentStep / steps.length) * 100;

  // If user doesn't have permission, show error
  if (!hasSchedulingPermission) {
    return (
      <Container maxW="container.lg" py={8}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              You don't have permission to create shifts. Please contact an administrator.
            </AlertDescription>
          </Box>
        </Alert>
        <Button mt={4} leftIcon={<ArrowBackIcon />} onClick={() => navigate("/shifts")}>
          Back to Shifts
        </Button>
      </Container>
    );
  }

  if (fetchingUsers) {
    return (
      <Container maxW="container.lg" py={8}>
        <Flex justify="center" align="center" minH="200px">
          <Spinner size="xl" />
          <Text ml={4}>Loading form data...</Text>
        </Flex>
      </Container>
    );
  }

  return (
    <Container maxW="container.lg" py={8}>
      {/* Header */}
      <HStack mb={6} spacing={4}>
        <IconButton
          icon={<ArrowBackIcon />}
          aria-label="Back to shifts"
          onClick={() => navigate("/shifts")}
          variant="ghost"
        />
        <Box>
          <Heading size="lg">Add New Shift</Heading>
          <Text color="gray.600">Create and schedule a new shift for students</Text>
        </Box>
        <Spacer />
        <HStack spacing={2}>
          <Button
            leftIcon={<CopyIcon />}
            variant="outline"
            size="sm"
            onClick={handleCopyShift}
          >
            Copy Existing
          </Button>
          <Button
            leftIcon={<CheckIcon />}
            variant="outline"
            size="sm"
            onClick={handleSaveDraft}
            colorScheme={isDraft ? "green" : "gray"}
          >
            {isDraft ? "Draft Saved" : "Save Draft"}
          </Button>
        </HStack>
      </HStack>

      {/* Progress Indicator */}
      <Card mb={6}>
        <CardBody>
          <Progress value={progress} colorScheme="blue" mb={4} />
          <HStack justify="space-between">
            {steps.map((step) => (
              <VStack key={step.number} spacing={1} flex={1}>
                <Badge
                  colorScheme={currentStep >= step.number ? "blue" : "gray"}
                  variant={currentStep === step.number ? "solid" : "outline"}
                  borderRadius="full"
                  px={3}
                  py={1}
                >
                  {step.number}
                </Badge>
                <Text fontSize="sm" fontWeight="medium" textAlign="center">
                  {step.title}
                </Text>
                <Text fontSize="xs" color="gray.500" textAlign="center">
                  {step.description}
                </Text>
              </VStack>
            ))}
          </HStack>
        </CardBody>
      </Card>

      {/* Main Form */}
      <form onSubmit={handleSubmit}>
        <Card bg={bgColor} borderColor={borderColor}>
          <CardBody>
            {/* Step 1: Basic Information */}
            {currentStep === 1 && (
              <VStack spacing={6} align="stretch">
                <Heading size="md" color="blue.600">
                  <CalendarIcon mr={2} />
                  Basic Shift Information
                </Heading>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  {/* Shift Date */}
                  <FormControl isRequired isInvalid={errors.shift?.shiftDate}>
                    <FormLabel>Shift Date</FormLabel>
                    <Input
                      type="datetime-local"
                      {...register("shift.shiftDate")}
                    />
                    <FormErrorMessage>
                      {errors.shift?.shiftDate?.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* End Time */}
                  <FormControl isRequired isInvalid={errors.shift?.endTime}>
                    <FormLabel>End Time</FormLabel>
                    <Input
                      type="datetime-local"
                      {...register("shift.endTime")}
                    />
                    <FormErrorMessage>
                      {errors.shift?.endTime?.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Institution */}
                  <FormControl isRequired isInvalid={errors.shift?.institution}>
                    <FormLabel>Institution</FormLabel>
                    <Select
                      placeholder="Select institution"
                      {...register("shift.institution")}
                    >
                      {institutions.map((institution) => (
                        <option key={institution} value={institution}>
                          {institution}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>
                      {errors.shift?.institution?.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Cohort */}
                  <FormControl isRequired isInvalid={errors.shift?.cohort}>
                    <FormLabel>Cohort</FormLabel>
                    <Select
                      placeholder="Select cohort"
                      {...register("shift.cohort")}
                    >
                      {cohorts.map((cohort) => (
                        <option key={cohort} value={cohort}>
                          {cohort}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>
                      {errors.shift?.cohort?.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Location */}
                  <FormControl isRequired isInvalid={errors.shift?.location}>
                    <FormLabel>Location</FormLabel>
                    <Input
                      placeholder="Enter location (e.g., General Hospital Emergency Dept)"
                      {...register("shift.location")}
                    />
                    <FormErrorMessage>
                      {errors.shift?.location?.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Unit/Department */}
                  <FormControl isRequired isInvalid={errors.shift?.unit}>
                    <FormLabel>Unit/Department</FormLabel>
                    <Input
                      placeholder="Enter unit (e.g., Emergency, ICU, Ambulance 12)"
                      {...register("shift.unit")}
                    />
                    <FormErrorMessage>
                      {errors.shift?.unit?.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Shift Type */}
                  <FormControl isRequired isInvalid={errors.shift?.shiftType}>
                    <FormLabel>Shift Type</FormLabel>
                    <Select
                      placeholder="Select shift type"
                      {...register("shift.shiftType")}
                    >
                      <option value="field">Field</option>
                      <option value="hospital">Hospital</option>
                      <option value="lab">Lab</option>
                      <option value="field_internship">Field Internship</option>
                    </Select>
                    <FormErrorMessage>
                      {errors.shift?.shiftType?.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Status */}
                  <FormControl isInvalid={errors.shift?.status}>
                    <FormLabel>Status</FormLabel>
                    <Select {...register("shift.status")}>
                      <option value="scheduled">Scheduled</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </Select>
                    <FormErrorMessage>
                      {errors.shift?.status?.message}
                    </FormErrorMessage>
                  </FormControl>
                </SimpleGrid>

                {/* Recurring Options (Future Feature) */}
                <Box p={4} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                  <HStack mb={3}>
                    <InfoIcon color="blue.500" />
                    <Text fontWeight="bold" color="blue.700">Recurring Shift Options (Coming Soon)</Text>
                  </HStack>
                  <VStack align="start" spacing={2}>
                    <HStack>
                      <Switch
                        isChecked={isRecurring}
                        onChange={(e) => setIsRecurring(e.target.checked)}
                        colorScheme="blue"
                        isDisabled
                      />
                      <Text color="gray.600">Make this a recurring shift</Text>
                    </HStack>
                    {isRecurring && (
                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="full">
                        <Select placeholder="Frequency" isDisabled>
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </Select>
                        <Input placeholder="End date" type="date" isDisabled />
                        <Input placeholder="Number of occurrences" type="number" isDisabled />
                      </SimpleGrid>
                    )}
                  </VStack>
                </Box>
              </VStack>
            )}

            {/* Step 2: Assignment */}
            {currentStep === 2 && (
              <VStack spacing={6} align="stretch">
                <Heading size="md" color="blue.600">
                  <AddIcon mr={2} />
                  Student & Preceptor Assignment
                </Heading>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  {/* Student Assignment */}
                  <FormControl isRequired isInvalid={errors.shift?.student}>
                    <FormLabel>Assign Student</FormLabel>
                    <Select
                      placeholder="Select student"
                      {...register("shift.student")}
                    >
                      {students.map((student) => (
                        <option key={student.id} value={student.id}>
                          {student.firstName} {student.lastName} - {student.email}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>
                      {errors.shift?.student?.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Preceptor Assignment */}
                  <FormControl isInvalid={errors.shift?.preceptor}>
                    <FormLabel>Assign Preceptor (Optional)</FormLabel>
                    <Select
                      placeholder="Select preceptor"
                      {...register("shift.preceptor")}
                    >
                      {preceptors.map((preceptor) => (
                        <option key={preceptor.id} value={preceptor.id}>
                          {preceptor.firstName} {preceptor.lastName} - {preceptor.email}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>
                      {errors.shift?.preceptor?.message}
                    </FormErrorMessage>
                  </FormControl>
                </SimpleGrid>

                {/* Future Scheduling Features */}
                <Box p={4} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                  <HStack mb={3}>
                    <InfoIcon color="green.500" />
                    <Text fontWeight="bold" color="green.700">Advanced Scheduling Features (Coming Soon)</Text>
                  </HStack>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <VStack align="start" spacing={2}>
                      <Text fontSize="sm" fontWeight="medium">Student Capacity</Text>
                      <Input placeholder="Max students (e.g., 2)" type="number" isDisabled />
                    </VStack>
                    <VStack align="start" spacing={2}>
                      <Text fontSize="sm" fontWeight="medium">Availability Window</Text>
                      <HStack>
                        <Input placeholder="Available from" type="date" size="sm" isDisabled />
                        <Input placeholder="Until" type="date" size="sm" isDisabled />
                      </HStack>
                    </VStack>
                  </SimpleGrid>
                </Box>
              </VStack>
            )}

            {/* Step 3: Additional Details */}
            {currentStep === 3 && (
              <VStack spacing={6} align="stretch">
                <Heading size="md" color="blue.600">
                  <InfoIcon mr={2} />
                  Additional Details & Notes
                </Heading>

                <SimpleGrid columns={1} spacing={6}>
                  {/* Objectives */}
                  <FormControl>
                    <FormLabel>Learning Objectives</FormLabel>
                    <Textarea
                      placeholder="Enter learning objectives for this shift..."
                      rows={4}
                      {...register("shift.objectives")}
                    />
                  </FormControl>

                  {/* Notes */}
                  <FormControl>
                    <FormLabel>Special Instructions/Notes</FormLabel>
                    <Textarea
                      placeholder="Enter any special instructions, prerequisites, or notes for students..."
                      rows={4}
                      {...register("shift.notes")}
                    />
                  </FormControl>

                  {/* Preceptor Notes */}
                  <FormControl>
                    <FormLabel>Preceptor Notes</FormLabel>
                    <Textarea
                      placeholder="Enter notes specifically for the preceptor..."
                      rows={3}
                      {...register("shift.preceptorNotes")}
                    />
                  </FormControl>

                  {/* Advanced Options */}
                  <Box>
                    <Button
                      variant="ghost"
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      mb={4}
                    >
                      {showAdvanced ? "Hide" : "Show"} Advanced Options
                    </Button>

                    {showAdvanced && (
                      <VStack spacing={4} align="stretch">
                        <HStack>
                          <FormControl>
                            <FormLabel fontSize="sm">Lock Shift</FormLabel>
                            <Switch {...register("shift.locked")} colorScheme="red" />
                          </FormControl>
                        </HStack>

                        {/* Future Export Options */}
                        <Box p={4} bg="purple.50" borderRadius="md" border="1px solid" borderColor="purple.200">
                          <Text fontWeight="bold" color="purple.700" mb={2}>
                            Integration Preparation (Coming Soon)
                          </Text>
                          <HStack spacing={4}>
                            <Button size="sm" variant="outline" colorScheme="purple" isDisabled>
                              Export to Calendar
                            </Button>
                            <Button size="sm" variant="outline" colorScheme="purple" isDisabled>
                              Send to LMS
                            </Button>
                            <Button size="sm" variant="outline" colorScheme="purple" isDisabled>
                              Notify Students
                            </Button>
                          </HStack>
                        </Box>
                      </VStack>
                    )}
                  </Box>
                </SimpleGrid>
              </VStack>
            )}

            {/* Step 4: Review */}
            {currentStep === 4 && (
              <VStack spacing={6} align="stretch">
                <Heading size="md" color="blue.600">
                  Review Shift Details
                </Heading>

                <Alert status="info">
                  <AlertIcon />
                  Please review all details before creating the shift.
                </Alert>

                {/* Review Form Data */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Card>
                    <CardHeader pb={2}>
                      <Text fontWeight="bold">Shift Information</Text>
                    </CardHeader>
                    <CardBody pt={0}>
                      <VStack align="start" spacing={2} fontSize="sm">
                        <Text><strong>Date:</strong> {watch("shift.shiftDate") || "Not set"}</Text>
                        <Text><strong>End Time:</strong> {watch("shift.endTime") || "Not set"}</Text>
                        <Text><strong>Location:</strong> {watch("shift.location") || "Not set"}</Text>
                        <Text><strong>Unit:</strong> {watch("shift.unit") || "Not set"}</Text>
                        <Text><strong>Type:</strong> {watch("shift.shiftType") || "Not set"}</Text>
                        <Text><strong>Status:</strong> {watch("shift.status") || "scheduled"}</Text>
                      </VStack>
                    </CardBody>
                  </Card>

                  <Card>
                    <CardHeader pb={2}>
                      <Text fontWeight="bold">Assignment</Text>
                    </CardHeader>
                    <CardBody pt={0}>
                      <VStack align="start" spacing={2} fontSize="sm">
                        <Text><strong>Institution:</strong> {watch("shift.institution") || "Not set"}</Text>
                        <Text><strong>Cohort:</strong> {watch("shift.cohort") || "Not set"}</Text>
                        <Text><strong>Student:</strong> {watch("shift.student") ? "Assigned" : "Not assigned"}</Text>
                        <Text><strong>Preceptor:</strong> {watch("shift.preceptor") ? "Assigned" : "Not assigned"}</Text>
                      </VStack>
                    </CardBody>
                  </Card>
                </SimpleGrid>

                {/* Template Options */}
                <Box p={4} bg="gray.50" borderRadius="md">
                  <HStack mb={3}>
                    <Text fontWeight="bold">Save as Template (Coming Soon)</Text>
                  </HStack>
                  <HStack>
                    <Input placeholder="Template name" isDisabled size="sm" />
                    <Button
                      leftIcon={<CheckIcon />}
                      onClick={handleSaveTemplate}
                      size="sm"
                      isDisabled
                    >
                      Save Template
                    </Button>
                  </HStack>
                </Box>
              </VStack>
            )}

            {/* Navigation Buttons */}
            <Divider my={6} />
            <HStack justify="space-between">
              <Button
                onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                isDisabled={currentStep === 1}
                variant="outline"
              >
                Previous
              </Button>

              <Text fontSize="sm" color="gray.500">
                Step {currentStep} of {steps.length}
              </Text>

              {currentStep < steps.length ? (
                <Button
                  onClick={() => setCurrentStep(Math.min(steps.length, currentStep + 1))}
                  colorScheme="blue"
                >
                  Next
                </Button>
              ) : (
                <Button
                  type="submit"
                  colorScheme="green"
                  isLoading={isSubmitting}
                  loadingText="Creating Shift..."
                >
                  Create Shift
                </Button>
              )}
            </HStack>
          </CardBody>
        </Card>
      </form>

      {/* Bulk Creation Helper (Future Feature) */}
      <Card mt={6} bg="yellow.50" borderColor="yellow.200">
        <CardBody>
          <HStack>
            <InfoIcon color="yellow.600" />
            <Box>
              <Text fontWeight="bold" color="yellow.800">Bulk Creation Helper</Text>
              <Text fontSize="sm" color="yellow.700">
                Need to create multiple similar shifts? The bulk creation tool will be available soon.
              </Text>
            </Box>
            <Spacer />
            <Button colorScheme="yellow" variant="outline" isDisabled size="sm">
              Coming Soon
            </Button>
          </HStack>
        </CardBody>
      </Card>
    </Container>
  );
}
